/**
 * The bag's Use action (consumables.md §4 "Client"; AD-791; `q-consumable-use-build` S3).
 * Pure helpers: the words for an `itemUseResult`, and the cooldown readout. The server
 * decides every effect and refusal; the client only says what happened and how long the
 * group is still cooling.
 */

import type { ConsumableCooldownGroup, ItemUseResultPayload } from '@great-cloud/shared/client';

/** Per-group `Date.now()` at which the group can be used again (absent ⇒ ready). */
export type ConsumableReadyAt = Partial<Record<ConsumableCooldownGroup, number>>;

/** The toast line for an `itemUseResult` refusal (the typed reason, in words). */
export function describeItemUseDenial(
  denied: Extract<ItemUseResultPayload, { ok: false }>['denied'],
  readyInMs?: number,
): string {
  switch (denied) {
    case 'onCooldown':
      return readyInMs !== undefined && readyInMs > 0
        ? `Not yet — potions ready in ${Math.ceil(readyInMs / 1000)}s.`
        : 'Not yet — potions are still cooling.';
    case 'inCombat':
      return 'Not while fighting.';
    case 'downed':
      return 'Not while down.';
    case 'fullHealth':
      return 'Nothing to mend.';
    case 'notConsumable':
      return 'That item cannot be used.';
    default:
      return 'That item is not in your bag.';
  }
}

/** The toast line for a successful use: what it restored, in words. */
export function itemUsedText(d: Extract<ItemUseResultPayload, { ok: true }>, name: string): string {
  const parts: string[] = [];
  if (d.healed > 0) parts.push(`+${d.healed} health`);
  if (d.stamina > 0) parts.push(`+${d.stamina} stamina`);
  return parts.length ? `Used ${name} (${parts.join(', ')})` : `Used ${name}`;
}

/** Whole seconds until `group` is ready at `nowMs` (0 = ready). */
export function cooldownLeftS(
  readyAt: ConsumableReadyAt,
  group: ConsumableCooldownGroup,
  nowMs: number,
): number {
  const at = readyAt[group];
  return at === undefined || at <= nowMs ? 0 : Math.ceil((at - nowMs) / 1000);
}

/** The Use button's label: `use`, or `use 12s` while the group cools. */
export function useLabel(leftS: number): string {
  return leftS > 0 ? `use ${leftS}s` : 'use';
}
